// import PsychoeducationSection from "../_shared/PsychoeducationSection"; // checkbox removed from the Psychotherapy Note encounter, so it is no longer printed
import {
  // BillingBlock, // Billing Information section removed
  // MseBlock, // old MSE mapping — replaced by <InitialPsychModalsMapper type="mse" />
  NestedText,
  PsychodynamicBlock,
  // RiskBlock, // old Risk mapping — replaced by <InitialPsychModalsMapper type="riskAssessment" />
  SubSection,
  labelList,
  splitCsv,
} from "../_shared/TherapyBlocks";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import RosBlock from "../_shared/RosBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import AssessmentBlock from "../_shared/AssessmentBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
import {
  CarePlanTemplateList,
  KeyValPair,
  OrderList,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
  TherapyNotesList,
  checkNull,
} from "../constant";
// Shared mapper for the 3 modals (Psychiatric Evolution, MSE, Risk Assessment)
// reused from the Initial Psych Evolution template.
import InitialPsychModalsMapper from "../initialPsychEvolutionTemplate/components/InitialPsychModalsMapper";
// CBT / DBT / ACT modality-specific sections mapped from the real nested keys.
import ModalitySpecificMapper from "./ModalitySpecificMapper";

interface PsychotherapyNoteTemplateProps {
  data: any;
}

const PRIMARY_MODALITY_LABELS: Record<string, string> = {
  Cognitive_Behavioral_Therapy_CBT: "Cognitive Behavioral Therapy (CBT)",
  Dialectical_Behavior_Therapy_DBT: "Dialectical Behavior Therapy (DBT)",
  Acceptance_Commitment_Therapy_ACT: "Acceptance & Commitment Therapy (ACT)",
  // Psychodynamic_Therapy: "Psychodynamic Therapy",
  // Trauma_Therapy: "Trauma Therapy",
  // Solution_Focused_Therapy: "Solution-Focused Therapy",
};

// OLD label maps for the flat-key CBT/DBT mappings — superseded by
// <ModalitySpecificMapper /> (which reuses the form's own constants). Kept for
// discoverability.
// const CBT_DISTORTION_LABELS: Record<string, string> = {
//   allOrNothing: "All-or-nothing thinking",
//   overgeneralization: "Overgeneralization",
//   mentalFilter: "Mental filter",
//   discountingPositive: "Discounting the positive",
//   jumpingToConclusions: "Jumping to conclusions",
//   emotionalReasoning: "Emotional reasoning",
//   magnificationMinimization: "Magnification/Minimization",
//   shouldStatements: "Should statements",
//   labeling: "Labeling",
//   personalization: "Personalization",
//   thoughtRecordCompleted: "Thought record completed during session",
// };

// const DBT_STRATEGY_LABELS: Record<string, string> = {
//   validation: "Validation",
//   behavioralChainAnalysis: "Behavioral chain analysis",
//   solutionAnalysis: "Solution analysis",
//   commitmentStrategies: "Commitment strategies",
//   dialecticalStrategies: "Dialectical strategies",
//   cognitiveModification: "Cognitive modification",
// };

const COPING_SKILL_LABELS: Record<string, string> = {
  grounding_techniques: "Grounding techniques",
  safe_place_visualization: "Safe place visualization",
  bilateral_stimulation: "Bilateral stimulation",
  self_soothing_activities: "Self-soothing activities",
  breathing_exercises: "Breathing exercises",
  container_exercise: "Container exercise",
  mindfulness: "Mindfulness",
  support_system_activation: "Support system activation",
};

const EMDR_PHASE_LABELS: Record<string, string> = {
  history_taking: "Phase 1: History Taking",
  preparation: "Phase 2: Preparation",
  assessment: "Phase 3: Assessment",
  desensitization: "Phase 4: Desensitization",
  installation: "Phase 5: Installation",
  body_scan: "Phase 6: Body Scan",
  closure: "Phase 7: Closure",
  reevaluation: "Phase 8: Reevaluation",
};

const DefaultModalityBlock = ({ data }: { data: any }) => {
  // OLD flat-key detection — the CBT/DBT/ACT data now lives under nested visit
  // note objects and is rendered by <ModalitySpecificMapper />. Kept for
  // discoverability.
  // const cbt =
  //   data?.cbtAutomaticThoughts ||
  //   data?.cbtBehavioralExperiments ||
  //   splitCsv(data?.cbtDistortions).length > 0;
  // const dbt =
  //   splitCsv(data?.dbtStrategies).length > 0 ||
  //   data?.dbtMindfulness ||
  //   data?.dbtDistressTolerance ||
  //   data?.dbtEmotionRegulation ||
  //   data?.dbtInterpersonal;
  // const act =
  //   data?.acceptanceWork ||
  //   data?.cognitiveDefusion ||
  //   data?.presentMomentAwareness ||
  //   data?.selfAsContext ||
  //   data?.valueClarification ||
  //   data?.commitedAction;

  return (
    <>
      {(data?.presentingConcerns || data?.sessionFocus) && (
        <SubSection label="Presenting Concerns & Session Focus">
          <NestedText
            label="Presenting Concerns"
            val={data?.presentingConcerns}
          />
          <NestedText label="Session Focus/Agenda" val={data?.sessionFocus} />
        </SubSection>
      )}

      {/* Old MSE mapping — now handled by the top-level MSE modal mapper
          (<InitialPsychModalsMapper type="mse" />). Kept for discoverability. */}
      {/* <MseBlock data={data} /> */}

      {/* CBT / DBT / ACT modality-specific sections — mapped from the real
          nested keys (psychotherapyCbtVisitNote / DbtVisitNote / ActVisitNote). */}
      <ModalitySpecificMapper data={data} />

      {/* OLD modality mappings (flat keys, no longer populated) — commented out:
      {cbt && (
        <SubSection label="CBT-Specific Components">
          <NestedText
            label="Automatic Thoughts Identified"
            val={data?.cbtAutomaticThoughts}
          />
          {splitCsv(data?.cbtDistortions).length > 0 && (
            <KeyValPair
              k="Cognitive Distortions Identified"
              v={labelList(
                splitCsv(data?.cbtDistortions),
                CBT_DISTORTION_LABELS
              )}
            />
          )}
          <NestedText
            label="Behavioral Experiments/Exposure Work"
            val={data?.cbtBehavioralExperiments}
          />
        </SubSection>
      )}

      {dbt && (
        <SubSection label="DBT-Specific Components">
          {splitCsv(data?.dbtStrategies).length > 0 && (
            <KeyValPair
              k="Dialectical Strategies Used"
              v={labelList(splitCsv(data?.dbtStrategies), DBT_STRATEGY_LABELS)}
            />
          )}
          <NestedText
            label="Mindfulness Exercises"
            val={data?.dbtMindfulness}
          />
          <NestedText
            label="Distress Tolerance Skills"
            val={data?.dbtDistressTolerance}
          />
          <NestedText
            label="Emotion Regulation Skills"
            val={data?.dbtEmotionRegulation}
          />
          <NestedText
            label="Interpersonal Effectiveness Skills"
            val={data?.dbtInterpersonal}
          />
        </SubSection>
      )}

      {act && (
        <SubSection label="ACT-Specific Components (Hexaflex)">
          <NestedText label="Acceptance Work" val={data?.acceptanceWork} />
          <NestedText
            label="Cognitive Defusion"
            val={data?.cognitiveDefusion}
          />
          <NestedText
            label="Present Moment Awareness"
            val={data?.presentMomentAwareness}
          />
          <NestedText label="Self as Context" val={data?.selfAsContext} />
          <NestedText
            label="Values Clarification"
            val={data?.valueClarification}
          />
          <NestedText label="Committed Action" val={data?.commitedAction} />
        </SubSection>
      )}

      {(data?.interventionsUsed || data?.clientReponseToInterventins) && (
        <SubSection label="Interventions & Client Response">
          <NestedText
            label="Interventions Used"
            val={data?.interventionsUsed}
          />
          <NestedText
            label="Client Response to Interventions"
            val={data?.clientReponseToInterventins}
          />
        </SubSection>
      )}

      {(data?.homeworkAssigned || data?.progressTowardTreatmentGoal) && (
        <SubSection label="Homework & Progress">
          <NestedText label="Homework Assigned" val={data?.homeworkAssigned} />
          <NestedText
            label="Progress Toward Treatment Goals"
            val={data?.progressTowardTreatmentGoal}
          />
        </SubSection>
      )}
      */}

      {data?.clinicalImpression && (
        <SubSection label="Clinical Impression & Plan">
          <NestedText
            label="Clinical Impression"
            val={data?.clinicalImpression}
          />
        </SubSection>
      )}

      {data?.planForNextSession && (
        <SubSection label="Plan for Next Session">
          <NestedText
            label="Plan for Next Session"
            val={data?.planForNextSession}
          />
        </SubSection>
      )}

      {/* Old Risk mapping — now handled by the top-level Risk Assessment modal
          mapper (<InitialPsychModalsMapper type="riskAssessment" />). Kept for
          discoverability. */}
      {/* <RiskBlock data={data} /> */}
      {/* Billing Information section removed */}
      {/* <BillingBlock data={data} /> */}
    </>
  );
};

const TraumaBlock = ({ data }: { data: any }) => {
  const copingSkills = splitCsv(data?.copingSkillsPracticed);
  return (
    <>
      {(data?.triggersIdentified ||
        data?.dissociativeAssessment ||
        data?.groundingTechniquesUsed ||
        data?.windowOfToleranceAssessment) && (
        <SubSection label="Trauma & Safety Assessment">
          <NestedText
            label="Triggers Identified"
            val={data?.triggersIdentified}
          />
          <NestedText
            label="Dissociation Assessment"
            val={data?.dissociativeAssessment}
          />
          <NestedText
            label="Grounding Techniques Used"
            val={data?.groundingTechniquesUsed}
          />
          <NestedText
            label="Window of Tolerance Assessment"
            val={data?.windowOfToleranceAssessment}
          />
        </SubSection>
      )}

      {(data?.emdrPhase ||
        data?.targetMemoryImage ||
        data?.negativeCognitionNc ||
        data?.positiveCognitionPc ||
        data?.vocValidityOfCognition ||
        data?.emotions ||
        data?.sudScale ||
        data?.bodySensationsLocations ||
        data?.desensitizationSetsProcessing ||
        data?.installationProcess ||
        data?.bodyScanResults ||
        data?.closure) && (
        <SubSection label="EMDR Protocol">
          {data?.emdrPhase && (
            <KeyValPair
              k="EMDR Phase"
              v={EMDR_PHASE_LABELS[data.emdrPhase] ?? data.emdrPhase}
            />
          )}
          <NestedText
            label="Target Memory/Image"
            val={data?.targetMemoryImage}
          />
          {data?.negativeCognitionNc && (
            <KeyValPair
              k="Negative Cognition (NC)"
              v={data.negativeCognitionNc}
            />
          )}
          {data?.positiveCognitionPc && (
            <KeyValPair
              k="Positive Cognition (PC)"
              v={data.positiveCognitionPc}
            />
          )}
          {data?.vocValidityOfCognition && (
            <KeyValPair
              k="VoC (Validity of Cognition) 1-7"
              v={data.vocValidityOfCognition}
            />
          )}
          {data?.emotions && <KeyValPair k="Emotions" v={data.emotions} />}
          {data?.sudScale && <KeyValPair k="SUD (0-10)" v={data.sudScale} />}
          {data?.bodySensationsLocations && (
            <KeyValPair
              k="Body Sensations/Locations"
              v={data.bodySensationsLocations}
            />
          )}
          <NestedText
            label="Desensitization (Sets & Processing)"
            val={data?.desensitizationSetsProcessing}
          />
          <NestedText
            label="Installation Process"
            val={data?.installationProcess}
          />
          <NestedText label="Body Scan Results" val={data?.bodyScanResults} />
          <NestedText label="Closure" val={data?.closure} />
        </SubSection>
      )}

      {(data?.traumaNarrativeWork ||
        data?.cognitiveProcessingOfTrauma ||
        data?.somaticBodyBasedInterventions) && (
        <SubSection label="Trauma Processing Work">
          <NestedText
            label="Trauma Narrative Work"
            val={data?.traumaNarrativeWork}
          />
          <NestedText
            label="Cognitive Processing of Trauma"
            val={data?.cognitiveProcessingOfTrauma}
          />
          <NestedText
            label="Somatic/Body-Based Interventions"
            val={data?.somaticBodyBasedInterventions}
          />
        </SubSection>
      )}

      {(data?.resourceInstallation ||
        checkNull(copingSkills) ||
        data?.stabilizationWork ||
        data?.safetyPlanning) && (
        <SubSection label="Resource Development & Stabilization">
          <NestedText
            label="Resource Installation"
            val={data?.resourceInstallation}
          />
          {checkNull(copingSkills) && (
            <KeyValPair
              k="Coping Skills Practiced"
              v={labelList(copingSkills, COPING_SKILL_LABELS)}
            />
          )}
          <NestedText
            label="Stabilization Work"
            val={data?.stabilizationWork}
          />
          <NestedText label="Safety Planning" val={data?.safetyPlanning} />
        </SubSection>
      )}

      {(data?.clientResponseToInterpretations ||
        data?.progressTowardTraumaTreatmentGoals) && (
        <SubSection label="Client Response & Progress">
          <NestedText
            label="Client Response to Interventions"
            val={data?.clientResponseToInterpretations}
          />
          <NestedText
            label="Progress Toward Trauma Treatment Goals"
            val={data?.progressTowardTraumaTreatmentGoals}
          />
        </SubSection>
      )}

      {(data?.clinicalImpressions ||
        data?.planForNextSession ||
        data?.riskAssessment) && (
        <SubSection label="Clinical Impression & Plan">
          <NestedText
            label="Clinical Impression"
            val={data?.clinicalImpressions}
          />
          <NestedText
            label="Plan for Next Session"
            val={data?.planForNextSession}
          />
          <NestedText label="Risk Assessment" val={data?.riskAssessment} />
        </SubSection>
      )}
    </>
  );
};

const SolutionFocusedBlock = ({ data }: { data: any }) => {
  return (
    <>
      {(data?.preferredFutureGoals || data?.miracleQuestionResponse) && (
        <SubSection label="Future-Focused & Miracle Question">
          <NestedText
            label="Preferred Future/Goals"
            val={data?.preferredFutureGoals}
          />
          <NestedText
            label="Miracle Question Response"
            val={data?.miracleQuestionResponse}
          />
        </SubSection>
      )}

      {(data?.exceptionsIdentified ||
        data?.copingQuestions ||
        data?.whatsAlreadyWorking) && (
        <SubSection label="Exceptions & Existing Solutions">
          <NestedText
            label="Exceptions Identified"
            val={data?.exceptionsIdentified}
          />
          <NestedText label="Coping Questions" val={data?.copingQuestions} />
          <NestedText
            label="What's Already Working"
            val={data?.whatsAlreadyWorking}
          />
        </SubSection>
      )}

      {(data?.scalingQuestionUsed ||
        data?.currentRating ||
        data?.previousSessionRating ||
        data?.indicatorsOfProgress) && (
        <SubSection label="Scaling Questions">
          <NestedText
            label="Scaling Question Used"
            val={data?.scalingQuestionUsed}
          />
          {data?.currentRating && (
            <KeyValPair k="Current Rating (0-10)" v={data.currentRating} />
          )}
          {data?.previousSessionRating && (
            <KeyValPair
              k="Previous Session Rating"
              v={data.previousSessionRating}
            />
          )}
          <NestedText
            label="Indicators of Progress"
            val={data?.indicatorsOfProgress}
          />
        </SubSection>
      )}

      {(data?.clientStrengthsAndResources || data?.complimentsOffered) && (
        <SubSection label="Strengths, Resources & Competencies">
          <NestedText
            label="Client Strengths & Resources"
            val={data?.clientStrengthsAndResources}
          />
          <NestedText
            label="Compliments Offered"
            val={data?.complimentsOffered}
          />
        </SubSection>
      )}

      {(data?.smallChangesNoticed ||
        data?.betweenSessionTasks ||
        data?.nextStepsActions) && (
        <SubSection label="Small Changes & Action Steps">
          <NestedText
            label="Small Changes Noticed"
            val={data?.smallChangesNoticed}
          />
          <NestedText
            label="Between-Session Tasks"
            val={data?.betweenSessionTasks}
          />
          <NestedText label="Next Steps/Actions" val={data?.nextStepsActions} />
        </SubSection>
      )}

      {(data?.goalsSetForThisSession || data?.progressTowardSessionGoals) && (
        <SubSection label="Session Goals & Achievement">
          <NestedText
            label="Goals Set for This Session"
            val={data?.goalsSetForThisSession}
          />
          <NestedText
            label="Progress Toward Session Goals"
            val={data?.progressTowardSessionGoals}
          />
        </SubSection>
      )}

      {data?.clientResponseToSession && (
        <SubSection label="Client Response & Progress">
          <NestedText
            label="Client Response to Session"
            val={data?.clientResponseToSession}
          />
        </SubSection>
      )}

      {(data?.clinicalImpressions ||
        data?.planForNextSession ||
        data?.riskAssessment) && (
        <SubSection label="Clinical Impression & Plan">
          <NestedText
            label="Clinical Impression"
            val={data?.clinicalImpressions}
          />
          <NestedText
            label="Plan for Next Session"
            val={data?.planForNextSession}
          />
          <NestedText label="Risk Assessment" val={data?.riskAssessment} />
        </SubSection>
      )}
    </>
  );
};

const PsychotherapyNoteTemplate = ({
  data,
}: PsychotherapyNoteTemplateProps) => {
  const modality: string = data?.primaryModality ?? "";
  const NEW_MODALITIES = [
    "Psychodynamic_Therapy",
    "Trauma_Therapy",
    "Solution_Focused_Therapy",
  ];
  const isNew = NEW_MODALITIES.includes(modality);

  return (
    <>
      {/* ── Session Information (Primary Modality) — shown at the top so it does
          not drift into the middle of the report (ECRH-5279). */}
      {modality && (
        <ReportContainer label="Session Information">
          <KeyValPair
            k="Primary Modality"
            v={PRIMARY_MODALITY_LABELS[modality] ?? modality}
          />
        </ReportContainer>
      )}

      {/* ═══════════════ SUBJECTIVE ═══════════════ */}
      <ReportSection label="Subjective">
        <RenderTextWithContainerPattern
          label="Reason for Visit"
          val={data?.subjectiveChiefComplaint}
        />
        <RenderTextWithContainerPattern
          label="HPI"
          val={data?.historyOfPresentIllness}
        />
        <RenderTextWithContainerPattern
          label="Subjective"
          val={data?.subjectiveFreeNote}
        />
        <AllergiesBlock data={data} />
        <HistoryBlock data={data} />
        <RenderTextWithContainerPattern label="Habits" val={data?.habits} />
        <MedicationsBlock data={data} />
        {/* Psychiatric Evolution: checked history options + per-section note. */}
        <InitialPsychModalsMapper data={data} type="psychiatricEvolution" />
      </ReportSection>

      {/* ═══════════════ OBJECTIVE ═══════════════ */}
      <ReportSection label="Objective">
        <RenderTextWithContainerPattern
          label="Objective"
          val={data?.objectiveFreeNote}
        />
        <VitalsBlock data={data} />
        <PhysicalExamBlock data={data} />
        <RosBlock data={data} />
        <ScreeningAssessmentBlock data={data} />
        {/* MSE: checked options + per-section note + overall note. */}
        <InitialPsychModalsMapper data={data} type="mse" />
        {/* Presenting Concerns / Session Focus + modality-specific components
            (CBT / DBT / ACT Hexaflex incl. Progress, Evidence, Homework). */}
        {!isNew && <DefaultModalityBlock data={data} />}
        {modality === "Psychodynamic_Therapy" && (
          <PsychodynamicBlock data={data} withExtras />
        )}
        {modality === "Trauma_Therapy" && <TraumaBlock data={data} />}
        {modality === "Solution_Focused_Therapy" && (
          <SolutionFocusedBlock data={data} />
        )}
      </ReportSection>

      {/* ═══════════════ ASSESSMENT ═══════════════ */}
      <ReportSection label="Assessment">
        <AssessmentBlock data={data} />
        {/* Risk Assessment: selected risk level + free-text note. */}
        <InitialPsychModalsMapper data={data} type="riskAssessment" />
      </ReportSection>

      {/* ═══════════════ PLAN ═══════════════ */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern label="Plan" val={data?.planFreeNote} />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <TherapyNotesList therapyNotes={data?.therapyNotes} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ═══════════════ FOLLOW UP & INSTRUCTIONS ═══════════════ */}
      <ReportSection label="Follow Up & Instructions">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />
        {/* Psychoeducation Session removed — the "Psychoeducation Session
            Required?" checkbox no longer exists on the Psychotherapy Note
            encounter, so its value must not print here.
        <PsychoeducationSection data={data} /> */}
      </ReportSection>
    </>
  );
};

export default PsychotherapyNoteTemplate;
